import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import {
  mdiTrashCanOutline,
  mdiPlus,
  mdiCalendarRefresh,
  mdiOpenInNew
} from '@mdi/js'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '../ui/dialog'
import {
  useIntervention,
  useInterventionDocuments,
  useInterventionEvents,
  useExpenses,
  useCreateExpense,
  useDeleteExpense,
  useCreateIntervention,
  type Intervention,
} from '../../hooks/useInterventions'
import { InterventionDocumentsSection, InterventionSectionTitle } from './InterventionDocumentsSection'
import { InterventionTimelineSection } from './InterventionTimelineSection'
import { Can } from '../../contexts/PermissionsContext'
import { EmptyState } from '../ui/empty-state'
import { SECTION_ICONS } from '../../lib/section-icons'

function fmtMoney(cents?: number | null, currency = 'EUR'): string {
  if (cents == null) return '—'
  return `${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}`
}

export function InterventionDetailDrawer({
  org,
  assetUid,
  interventionUid,
  open,
  onOpenChange,
}: {
  org: string
  assetUid: string
  interventionUid: string | null
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation('assets')
  const uid = interventionUid ?? undefined
  const { data: iv } = useIntervention(org, uid)
  const { data: documents = [] } = useInterventionDocuments(org, uid)
  const { data: events = [] } = useInterventionEvents(org, uid)
  const { data: expenses = [] } = useExpenses(org, uid)

  const actuals = useMemo(
    () => expenses.reduce((sum, e) => sum + (e.amount || 0), 0),
    [expenses],
  )

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-xl"
        data-testid="intervention-detail-drawer"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{iv?.reference}</span>
            <span>{iv?.title}</span>
          </SheetTitle>
          {iv && (
            <Link
              to="/$org/interventions/$interventionUid"
              params={{ org, interventionUid: iv.uid }}
              className="inline-flex w-fit items-center gap-1 text-xs text-primary hover:underline"
              data-testid="intervention-drawer-open-full-page"
            >
              <AppIcon path={mdiOpenInNew} size="xs" />
              {t('interventions.detail.open_full_page', { defaultValue: 'Ouvrir la fiche complète' })}
            </Link>
          )}
        </SheetHeader>

        {!iv ? (
          <div className="p-4 text-sm text-muted-foreground">{t('interventions.detail.loading', { defaultValue: 'Chargement…' })}</div>
        ) : (
          <div className="space-y-6 pb-10">
            {/* Summary: estimate vs actual */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border bg-card p-3" data-testid="intervention-estimate">
                <div className="text-xs text-muted-foreground">{t('interventions.detail.estimate', { defaultValue: 'Estimation' })}</div>
                <div className="text-lg font-semibold">{fmtMoney(iv.cost_amount, iv.cost_currency || 'EUR')}</div>
              </div>
              <div className="rounded-lg border border-border bg-card p-3" data-testid="intervention-actuals">
                <div className="text-xs text-muted-foreground">{t('interventions.detail.actuals', { defaultValue: 'Dépenses réelles' })}</div>
                <div className="text-lg font-semibold">{fmtMoney(actuals)}</div>
              </div>
            </div>

            <PlanNextSection org={org} assetUid={assetUid} iv={iv} />

            <InterventionDocumentsSection org={org} assetUid={iv.asset_uid} interventionUid={iv.uid} documents={documents} />

            <ExpensesSection org={org} assetUid={iv.asset_uid} interventionUid={iv.uid} />

            <InterventionTimelineSection events={events} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

// --- Plan the next occurrence (§3) ---
function PlanNextSection({ org, assetUid, iv }: { org: string; assetUid: string; iv: Intervention }) {
  const { t } = useTranslation('assets')
  const createMut = useCreateIntervention(org, assetUid)
  const [planned, setPlanned] = useState(false)
  if (iv.kind !== 'preventive') return null
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <InterventionSectionTitle icon={mdiCalendarRefresh}>{t('interventions.detail.recurrence', { defaultValue: 'Récurrence' })}</InterventionSectionTitle>
      {planned ? (
        <p className="text-sm text-success" data-testid="intervention-plan-next-done">
          {t('interventions.detail.next_planned', { defaultValue: 'Prochaine occurrence planifiée.' })}
        </p>
      ) : (
        <Button
          size="sm"
          variant="outline"
          data-testid="intervention-plan-next-btn"
          disabled={createMut.isPending}
          onClick={() =>
            createMut.mutate(
              {
                asset_uid: iv.asset_uid,
                kind: iv.kind,
                category: iv.category,
                status: 'requested',
                title: iv.title,
                contract_uid: iv.contract_uid ?? null,
                provider_user_uid: iv.provider_user_uid ?? null,
                previous_intervention_uid: iv.uid,
              },
              { onSuccess: () => setPlanned(true) },
            )
          }
        >
          <AppIcon path={mdiPlus} size="sm" className="mr-1" />
          {t('interventions.detail.plan_next', { defaultValue: 'Planifier la prochaine' })}
        </Button>
      )}
    </div>
  )
}

// --- Expenses (§7) ---
function ExpensesSection({ org, assetUid, interventionUid }: { org: string; assetUid: string; interventionUid: string }) {
  const { t } = useTranslation('assets')
  const { data: expenses = [] } = useExpenses(org, interventionUid)
  const createMut = useCreateExpense(org, interventionUid)
  const deleteMut = useDeleteExpense(org, interventionUid)
  const [addOpen, setAddOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [amountEur, setAmountEur] = useState('')
  const [expenseDate, setExpenseDate] = useState('')

  const submit = () => {
    const amount = amountEur ? Math.round(parseFloat(amountEur) * 100) : 0
    createMut.mutate(
      {
        intervention_uid: interventionUid,
        asset_uid: assetUid,
        label,
        amount,
        currency: 'EUR',
        expense_date: expenseDate || null,
      },
      {
        onSuccess: () => {
          setAddOpen(false)
          setLabel('')
          setAmountEur('')
          setExpenseDate('')
        },
      },
    )
  }

  return (
    <div>
      <InterventionSectionTitle icon={SECTION_ICONS.expenses} testid="intervention-expenses-title">
        {t('interventions.detail.expenses', { defaultValue: 'Dépenses' })}
      </InterventionSectionTitle>
      {expenses.length === 0 ? (
        <EmptyState
          variant="inline"
          className="py-3"
          description={t('interventions.detail.no_expenses', { defaultValue: 'Aucune dépense enregistrée.' })}
          data-testid="intervention-expenses-empty"
        />
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {expenses.map((e) => (
            <li key={e.uid} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`intervention-expense-${e.uid}`}>
              <Link to="/$org/expenses/$expenseUid" params={{ org, expenseUid: e.uid }} className="flex-1 truncate hover:underline">
                {e.label}
              </Link>
              <span className="text-muted-foreground">{e.expense_date ? new Date(e.expense_date).toLocaleDateString() : ''}</span>
              <span className="font-medium">{fmtMoney(e.amount, e.currency)}</span>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('interventions.detail.delete_expense', { defaultValue: 'Supprimer' })}
                title={t('interventions.detail.delete_expense', { defaultValue: 'Supprimer' })}
                data-testid={`intervention-expense-delete-${e.uid}`}
                onClick={() => deleteMut.mutate(e.uid)}
              >
                <AppIcon path={mdiTrashCanOutline} size="xs" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2">
        {/* `exact` for the same reason as "Plan an intervention": a new finance
            line is stamped with no owner, so `finance.write_own` can never
            satisfy the create (expenses/service.go, financeLineScope). */}
        <Can perm="finance.write" exact>
          <Button size="sm" variant="outline" data-testid="intervention-expense-add-btn" onClick={() => setAddOpen(true)}>
            <AppIcon path={mdiPlus} size="sm" className="mr-1" />
            {t('interventions.detail.add_expense', { defaultValue: 'Ajouter une dépense' })}
          </Button>
        </Can>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('interventions.detail.add_expense', { defaultValue: 'Ajouter une dépense' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('interventions.detail.expense_label', { defaultValue: 'Libellé' })}</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="intervention-expense-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('interventions.detail.expense_amount', { defaultValue: 'Montant TTC (€)' })}</Label>
                <Input type="number" value={amountEur} onChange={(e) => setAmountEur(e.target.value)} data-testid="intervention-expense-amount" />
              </div>
              <div>
                <Label>{t('interventions.detail.expense_date', { defaultValue: 'Date' })}</Label>
                <Input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} data-testid="intervention-expense-date" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              {t('interventions.action.cancel', { defaultValue: 'Annuler' })}
            </Button>
            <Button onClick={submit} disabled={!label || !amountEur} data-testid="intervention-expense-submit">
              {t('interventions.action.save', { defaultValue: 'Enregistrer' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default InterventionDetailDrawer
